import React, {FC} from 'react';
import {connect} from 'react-redux';

import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../libs/shared/core/src/services/tracking/tracking';
import {getIsUserGlobalAdmin} from '../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {buildTeamCounterKey, findBadgeCounter} from '../../helpers/counterHelpers';
import {buildTeamPath} from '../../helpers/modelHelpers';
import {CountersVisibilityModesEnum, StartGuideTaskTypesEnum} from '../../models/api/preferencesModel';
import {SearchHelpCenterResultModel} from '../../models/api/searchHelpCenterModel';
import {InboxesFiltersEnum, TeamsFoldersEnum} from '../../models/url/models/inboxesUrlModel';
import {useNavigateToAdminHome} from '../../settings/home/helpers';
import {makeGetComposeChannelExcludingDemo} from '../../store/data/channels/channelsSelectors';
import {getCompany} from '../../store/data/company/companySelectors';
import {getCounters} from '../../store/data/dataSelectors';
import {updateHelpCenterSearchCacheAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getHelpCenterSearchCache} from '../../store/data/localPreferences/localPreferencesSelectors';
import {composeMessageAction} from '../../store/data/messages/draftMessagesActions';
import {makeGetCountersVisibility} from '../../store/data/preferences/countersSelectors';
import {updateStartGuideTasksAction} from '../../store/data/preferences/preferencesActions';
import {getUser, getUserNamespace} from '../../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf, getLastUpdatedTeam} from '../../store/data/teams/teamsSelectors';
import {openHelpChatAction} from '../../store/panels/app/appPanelActions';
import {makeGetAppPath} from '../../store/panels/app/appPanelSelectors';
import {makeGetAppPanelIdFromPanel} from '../../store/panels/panelsSelectors';
import {blogUrl, navigateToLinkAction} from '../../store/stateless/navigationActions';
import {getShouldAddTeamInbox} from '../../store/stateless/onboardingSelectors';
import {StartGuide, StartGuideProps} from './startGuide';
import {composeTeamInboxAccessRequest} from './startGuideActions';
import {useStartGuideHelper} from './startGuideHelpers';

/*
 * Props.
 */

interface StartGuideContainerProps {
  panelId: ViewerPanelId;
}

/*
 * Redux.
 */

function makeGetTeamNamespace() {
  return createSelector([getLastUpdatedTeam], (team) => team?.namespace || '');
}

function makeGetHasConversationsInTeamInbox(
  getCountersVisibility: AppParametricSelector<any, CountersVisibilityModesEnum>,
) {
  return createSelector(
    [getCountersVisibility, getCounters, getLastUpdatedTeam],
    (countersVisibility, counters, lastUpdatedTeam) => {
      if (!lastUpdatedTeam) {
        return false;
      }

      const key = buildTeamCounterKey(lastUpdatedTeam.id, countersVisibility);
      if (!key) {
        return false;
      }

      return Boolean(findBadgeCounter(counters, countersVisibility, key));
    },
  );
}

const mapStateToProps = makeMapStateToProps<StartGuideContainerProps>()(() => {
  const getTeamNamespace = makeGetTeamNamespace();
  const getCountersVisibility = makeGetCountersVisibility(getTeamNamespace);

  return {
    user: getUser,
    userNamespace: getUserNamespace,
    lastUpdatedTeam: getLastUpdatedTeam,
    hasConversationsInTeamInbox: makeGetHasConversationsInTeamInbox(getCountersVisibility),
    shouldAddTeamInbox: getShouldAddTeamInbox,
    emailComposeChannel: makeGetComposeChannelExcludingDemo({
      getSpecifiedMessageType: () => MessageTypesEnum.EMAIL,
    }),
    appPath: makeGetAppPath(makeGetAppPanelIdFromPanel()),
    company: getCompany,
    firstTeamUserIsAdminOf: getFirstTeamUserIsAdminOf,
    savedHelpCenterCache: getHelpCenterSearchCache,
    isGlobalAdmin: getIsUserGlobalAdmin,
  };
});

/*
 * Container component.
 */

interface StartGuideContainerComponentProps
  extends Omit<
    StartGuideProps,
    | 'tasks'
    | 'shouldShowSuccessScreen'
    | 'shouldShowStartGuideTasks'
    | 'navigateToAdminHome'
    | 'isUserFirstGlobalAdmin'
  > {
  panelId: ViewerPanelId;
  appPath: string;
}

const StartGuideContainerComponent: FC<StartGuideContainerComponentProps> = (props) => {
  const {tasks, shouldShowSuccessScreen, shouldShowStartGuideTasks} = useStartGuideHelper({
    appPath: props.appPath,
    composeTeamInboxAccessRequest: props.composeTeamInboxAccessRequest,
  });
  const navigateToAdminHome = useNavigateToAdminHome();
  return (
    <StartGuide
      tasks={tasks}
      shouldShowSuccessScreen={shouldShowSuccessScreen}
      shouldShowStartGuideTasks={shouldShowStartGuideTasks}
      givenName={props.givenName}
      isGlobalAdmin={props.isGlobalAdmin}
      company={props.company}
      companyName={props.companyName}
      userNamespace={props.userNamespace}
      hasEmailComposeChannel={props.hasEmailComposeChannel}
      shouldAddTeamInbox={props.shouldAddTeamInbox}
      shouldShowOpenTeamInbox={props.shouldShowOpenTeamInbox}
      composeTeamInboxAccessRequest={props.composeTeamInboxAccessRequest}
      openEmailComposer={props.openEmailComposer}
      navigateToTeamInbox={props.navigateToTeamInbox}
      navigateToAdminHome={navigateToAdminHome}
      onDismissSuccessScreen={props.onDismissSuccessScreen}
      onOpenHelpChatClick={props.onOpenHelpChatClick}
      onOpenBlogClick={props.onOpenBlogClick}
      navigate={props.navigate}
      initialSearchCache={props.initialSearchCache}
      onSaveSearchQuery={props.onSaveSearchQuery}
    />
  );
};

export const StartGuideContainer = connect(
  mapStateToProps,
  {
    navigateToLinkAction,
    composeTeamInboxAccessRequest,
    composeMessageAction,
    updateStartGuideTasksAction,
    openHelpChat: openHelpChatAction,
    updateHelpCenterSearchCache: updateHelpCenterSearchCacheAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {panelId} = ownProps;
    const {
      user,
      userNamespace,
      lastUpdatedTeam,
      hasConversationsInTeamInbox,
      shouldAddTeamInbox,
      emailComposeChannel,
      appPath,
      company,
      isGlobalAdmin,
    } = stateProps;

    // Link for contact us
    const onOpenHelpChatClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASK_CLICK, {
        category: 'help',
        user_track: isGlobalAdmin ? 'admin' : 'non_admin',
        item: 'talk_to_us',
      });
      dispatchProps.openHelpChat(panelId);
    };

    // Link for our blog
    const onOpenBlogClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASK_CLICK, {
        category: 'help',
        user_track: isGlobalAdmin ? 'admin' : 'non_admin',
        item: 'our_blog',
      });
      dispatchProps.navigateToLinkAction(blogUrl);
    };

    const companyName = company.name && company.name !== '[Unknown Company Name]' ? company.name : null;

    const onDismissSuccessScreen = () =>
      dispatchProps.updateStartGuideTasksAction(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS);

    const navigateToTeamInbox = () => {
      if (!lastUpdatedTeam) {
        return;
      }

      const teamInboxPath = buildTeamPath(
        lastUpdatedTeam.id,
        TeamsFoldersEnum.INBOX,
        InboxesFiltersEnum.OPEN,
      );
      dispatchProps.navigateToLinkAction(teamInboxPath);
    };

    const openEmailComposer = () => {
      if (emailComposeChannel) {
        dispatchProps.composeMessageAction({channelId: emailComposeChannel.id});
      }
    };

    const navigate = (destination: string) => () => dispatchProps.navigateToLinkAction(destination);

    const onSaveSearchQuery = (query?: string, result?: SearchHelpCenterResultModel) => {
      dispatchProps.updateHelpCenterSearchCache(query, result);
    };

    return {
      givenName: user.givenName,
      company,
      companyName,
      userNamespace,
      hasEmailComposeChannel: Boolean(emailComposeChannel),
      shouldAddTeamInbox,
      shouldShowOpenTeamInbox: hasConversationsInTeamInbox,
      composeTeamInboxAccessRequest: dispatchProps.composeTeamInboxAccessRequest,
      onDismissSuccessScreen,
      openEmailComposer,
      navigateToTeamInbox,
      onOpenHelpChatClick,
      onOpenBlogClick,
      navigate,
      initialSearchCache: stateProps.savedHelpCenterCache,
      onSaveSearchQuery,
      appPath,
      isGlobalAdmin,
      panelId,
    };
  },
)(StartGuideContainerComponent);
